import { createAsyncThunk } from '@reduxjs/toolkit';
import { DELIVERY_SALE_TYPES } from '../constants/saleType';

import {
  getStagedPurchases,
  getBufferPickupTime,
  getSaleType,
  getBufferDeliveryTime,
  getRootCategory,
} from '../selectors';

import { setPickupTime } from '../operations';

/**
 * # validateFutureMenuItems
 *
 * ### What it does:
 * - It checks if the items in your cart fit in to the time you have chosen in the future.
 * - It runs in when the user changes their pickup time / delivery time or when the user
 * enters the placeOrder screen.
 *
 * ### Scenarios:
 * - If it's:
 *    - Delivery: It would warn the user that these invalid items will be removed from the
 *                cart when you press confirm.
 *    - Catering: It would warn the user their original time, and their new time that they
 *                have chosen. When they press confirm the new time they picked
 *                (bufferPickupTime) will be applied and the items will be removed from the
 *                cart. They are also given the option to not apply this new time they have
 *                picked.
 *
 * ### Notes:
 * There is a thunk on both Web and Mobile under the same name that calls this thunk to
 * share the logic between both platforms but handle how the modal is shown differently.
 */
export const validateFutureMenuItems = createAsyncThunk(
  'validateFutureMenuItems',
  async (
    data: {
      onInvalidItemsExists: ({
        invalidItems,
        proceedWithChosenTime,
      }: {
        invalidItems: {
          id: string;
          name: string;
        }[];
        proceedWithChosenTime: () => void;
      }) => void;
      desiredDeliveryTime: string | null;
    },
    { dispatch, getState, rejectWithValue },
  ) => {
    const { onInvalidItemsExists, desiredDeliveryTime } = data;
    try {
      const state = getState() as EntireFrontendState;

      const saleType = getSaleType(state);
      const bufferPickupTime = getBufferPickupTime(state);
      const bufferDeliveryTime = getBufferDeliveryTime(state);
      const deliveryTime = desiredDeliveryTime || bufferDeliveryTime;

      const stagedPurchases = getStagedPurchases(state);

      const isDelivery = DELIVERY_SALE_TYPES.includes(saleType);

      const safeBufferPickupTime = bufferPickupTime || 'ASAP';
      const selectedScheduledTime = isDelivery
        ? deliveryTime
        : safeBufferPickupTime;

      const currentDay =
        selectedScheduledTime === 'ASAP'
          ? new Date()
              .toLocaleString('en-US', { weekday: 'long' })
              .toUpperCase()
          : new Date(selectedScheduledTime as string)
              .toLocaleString('en-US', { weekday: 'long' })
              .toUpperCase();

      const currentTime =
        selectedScheduledTime === 'ASAP'
          ? new Date().toTimeString().slice(0, 8)
          : new Date(selectedScheduledTime as string)
              .toTimeString()
              .slice(0, 8); // Get time in HH:MM:SS format

      const invalidItems = stagedPurchases
        .filter(purchase => {
          if (currentDay && currentTime) {
            const isWithinDay =
              purchase?.item.days.length && purchase?.item.availability
                ? purchase?.item.days
                    .map(day => String(day))
                    .includes(currentDay)
                : true;
            const isWithinTime =
              purchase?.item.start &&
              purchase?.item.end &&
              purchase?.item.availability
                ? currentTime >= purchase?.item.start &&
                  currentTime <= purchase?.item.end
                : true; // No time constraint if start or end is null

            let isWithinDayCategory = true;
            let isWithinTimeCategory = true;
            if (isWithinDay || isWithinTime) {
              const rootCategory = getRootCategory(state);
              const categoryOfPurchase = rootCategory?.subCategories.filter(
                subCategory => subCategory.id === purchase?.mimCategoryId,
              );
              isWithinDayCategory =
                categoryOfPurchase!.length &&
                categoryOfPurchase![0].days.length &&
                categoryOfPurchase![0].availability
                  ? categoryOfPurchase![0].days
                      .map(day => String(day))
                      .includes(currentDay)
                  : true;
              isWithinTimeCategory =
                categoryOfPurchase!.length &&
                categoryOfPurchase![0].start &&
                categoryOfPurchase![0].end &&
                categoryOfPurchase![0].availability
                  ? currentTime >= categoryOfPurchase![0].start &&
                    currentTime <= categoryOfPurchase![0].end
                  : true; // No time constraint if start or end is null
            }
            return (
              !isWithinDay ||
              !isWithinTime ||
              !isWithinDayCategory ||
              !isWithinTimeCategory
            ); // Filter out items not available now
          }
        })
        .map(({ id, item }) => ({ id, name: item.name }));

      // This is meant to be for non delivery orders (pickup and catering)
      const proceedWithChosenTime = () => {
        dispatch(setPickupTime(safeBufferPickupTime));
      };

      if (invalidItems.length) {
        onInvalidItemsExists({ invalidItems, proceedWithChosenTime });
      } else {
        proceedWithChosenTime();
      }
    } catch (e) {
      console.error(
        'Polygon Ordering: validateFutureMenuItems thunk failed',
        e,
      );
      return rejectWithValue(e);
    }
  },
);
